import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PasswordFieldType, FieldData, SecretFieldType } from 'types/FieldTypes';
import FormFieldContext from '../FormFieldContext';
import { useGlobalContext } from 'GlobalContext';
import FieldWrapper from '../FieldWrapper';
import FieldViewMode from './elements/FieldViewMode';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';
import '../../../style/scss/tooltip.scss';

interface SecretFieldProps extends SecretFieldType {
    name: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    viewInEditMode?: boolean;
    shouldAutoFocus?: boolean;
    errorMessage?: string | string[] | undefined;
}

const SecretField: React.FC<SecretFieldProps & { data: FieldData, viewKey: string }> = ({ 
    viewKey, name, label, data, helperText, tooltipText, placeholder, onChange, viewInEditMode, shouldAutoFocus,
    disabled, alignment, isEditable
}) => {
    const { t } = useTranslation();
    const { editing, updatedData, setUpdatedData, handleSubmit } = useContext(FormFieldContext);
    const { errorMessages, setUnsavedChanges } = useGlobalContext();
    const [value, setValue] = useState('');

    // Set the current value
    useEffect(() => {
        // Show dotted mask if value is true (a secret exists)
        if (data && data[name] === true) {
            setValue('••••••••••••');

        // Show nothing if no secret exists
        } else if (data && data[name] === false) {
            setValue('');

        // Show the value
        } else if (data && typeof data[name] === 'string') {
            setValue(data[name]);
        }
    }, [data, name]);

    // Updates the value after changing the input
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        if (onChange) {
            onChange(e);
        }
        setUnsavedChanges(viewKey, e.target.value !== data[name]);
        setUpdatedData({...updatedData, [name]: e.target.value});
    };

    // Submits the value after pressing enter
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (handleSubmit) handleSubmit();
        }
    }

    // Get the error message from the errorState
    const errorMessage = errorMessages[name];

    return (
        <FieldWrapper
            name={name}
            label={label}
            tooltipText={tooltipText}
            helperText={helperText}
            isEditable={isEditable}
            disabled={disabled}
            viewInEditMode={viewInEditMode}
            alignment={alignment}>
            {(editing || viewInEditMode) ? (
                // Edit mode
                <input
                    type="password"
                    id={name}
                    name={name}
                    placeholder={t(placeholder)}
                    value={value}
                    disabled={disabled}
                    onChange={handleInputChange}
                    className={errorMessage ? 'text is-invalid' : 'text'}
                    autoFocus={shouldAutoFocus}
                    onKeyDown={handleKeyDown}
                    autoComplete="new-password"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                />
            ) : (
                // View mode
                <FieldViewMode
                    value={value} 
                    alignment={alignment}
                />
            )}
        </FieldWrapper>
    );
};

export default SecretField;